// src/features/analytics/useAnalyticsSubaccounts.ts

import { useQuery } from '@tanstack/react-query';
import { AnalyticsApi } from './AnalyticsApi';
import {
  ANALYTICS_QUERY_KEYS,
  SUBACCOUNTS_DEFAULT_LIMIT,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import type { UtcRange } from '@/shared/utils/utcRange';
import type { AnalyticsEnvelope, SubaccountsData } from './types';

export interface UseAnalyticsSubaccountsResult {
  subaccounts: AnalyticsEnvelope<SubaccountsData> | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/** Network leaderboard — network scope only. Pass null cid to disable. */
export function useAnalyticsSubaccounts(
  cid: number | null,
  range: UtcRange,
  metric: AnalyticsMetric,
  limit: number = SUBACCOUNTS_DEFAULT_LIMIT
): UseAnalyticsSubaccountsResult {
  const query = useQuery({
    queryKey: ANALYTICS_QUERY_KEYS.subaccounts(cid ?? 0, range.from, range.to, metric, limit),
    queryFn: () => AnalyticsApi.getSubaccounts(cid!, range, metric, limit),
    enabled: cid !== null,
    staleTime: 60_000,
  });

  return {
    subaccounts: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
